import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

/**
 * Politique de confidentialité SunMatch — page publique.
 *
 * Cette page est accessible **sans consentement RGPD applicatif** (cf.
 * `PUBLIC_PATHS` dans `AppLayout`). Elle reprend la substance juridique de
 * `docs/compliance/politique-confidentialite.md` sous une forme lisible
 * utilisateur.
 *
 * Version juridique : v2.4 (reset mot de passe par e-mail, épopée F2 /
 * ADR-017 + ADR-018 — ni finalité ni base légale nouvelle : opération de la
 * gestion de compte ; le fait nouveau est un **destinataire**, Scaleway SAS,
 * premier vrai sous-traitant art. 28 à qui l'on transmet effectivement
 * l'adresse e-mail — à distinguer de Google, responsable indépendant qui ne
 * reçoit rien). Doit rester alignée sur `CONSENT_VERSION`
 * (`backend/app/config.py` + `ConsentModal`) et sur le texte d'autorité
 * `docs/compliance/politique-confidentialite.md`. Tout changement de fond ici
 * impose un bump de `CONSENT_VERSION` (intégrité de version art. 7.1 ; un ajout
 * de finalité 6.1.f est une mise à jour d'information, pas un re-consentement).
 *
 * ⚠️ Cette page est une **copie manuelle** du `.md`, pas un rendu : à chaque
 * bump, y porter le **contenu**, pas seulement le numéro de version — sinon la
 * politique *publiée* diverge du texte d'autorité (piège relevé en S33-8).
 *
 * Principe rédactionnel : décrire les traitements en termes compréhensibles
 * par l'utilisateur, PAS la stack technique. On nomme les destinataires /
 * sous-traitants (exigence RGPD art. 13/14) mais jamais les bibliothèques,
 * algorithmes ou chemins d'API internes.
 */
export function PrivacyPolicy(): JSX.Element {
  return (
    <article className="mx-auto max-w-3xl py-8">
      <Card className="space-y-6 p-8">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Politique de confidentialité
          </h1>
          <p className="text-xs text-slate-500">
            Version v2.4 — Dernière mise à jour : 17 juillet 2026
          </p>
        </header>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            1. Responsable de traitement
          </h2>
          <p className="text-sm leading-relaxed text-slate-700">
            SunMatch est un service en ligne gratuit et non commercial développé
            par <strong>Matthieu Gross</strong>, agissant en qualité de
            responsable de traitement (article 4.7 RGPD).
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            Contact RGPD :{" "}
            <a
              href="mailto:privacy@sunmatch.fr"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              privacy@sunmatch.fr
            </a>
            .
          </p>
          <p className="text-xs italic text-slate-500">
            DPO non désigné — la désignation d'un Délégué à la Protection des
            Données n'est pas obligatoire au sens de l'article 37 RGPD pour ce
            traitement (pas de traitement à grande échelle, pas de données
            sensibles au sens de l'article 9 RGPD).
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            2. Comment vous utilisez SunMatch
          </h2>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>
              <strong>Sans compte</strong> : une session anonyme est créée
              automatiquement, identifiée par un identifiant technique aléatoire.
              Aucun nom ni email n'est collecté.
            </li>
            <li>
              <strong>Avec un compte</strong> : vous créez un compte (email +
              mot de passe) pour retrouver vos sites et simulations d'un appareil
              à l'autre et dans le temps. À l'inscription, vous pouvez choisir de
              rattacher à votre compte les données de votre session anonyme. Si
              vous oubliez votre mot de passe, vous pouvez en demander la
              réinitialisation : nous vous envoyons alors, à l'adresse email de
              votre compte, un <strong>lien à usage unique valable 1 heure</strong>{" "}
              (cet envoi fait intervenir un prestataire d'envoi d'emails, voir
              § 5).
            </li>
            <li>
              <strong>Avec un compte, en vous connectant via Google</strong> : au
              lieu d'un mot de passe, vous pouvez choisir « Continuer avec Google
              ». SunMatch récupère alors, auprès de Google et avec votre accord,
              votre <strong>adresse email vérifiée</strong> et un{" "}
              <strong>identifiant Google stable</strong> — rien d'autre (ni nom,
              ni photo de profil). Cette option est facultative : la connexion par
              email et mot de passe reste toujours disponible.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            3. Données collectées
          </h2>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>
              <strong>Compte</strong> (si vous en créez un) : adresse email et
              mot de passe.
            </li>
            <li>
              <strong>Connexion via Google</strong> (si vous choisissez «
              Continuer avec Google ») : adresse email <strong>vérifiée</strong>{" "}
              et <strong>identifiant Google stable</strong> (<code>sub</code>),
              transmis par Google lors de votre connexion, avec votre accord.{" "}
              <strong>Aucune autre donnée Google</strong> (nom, photo, contacts…)
              n'est demandée ni conservée.
            </li>
            <li>
              <strong>Réinitialisation du mot de passe</strong> (si vous en
              faites la demande) : un <strong>lien à usage unique</strong> envoyé
              à l'adresse email de votre compte. Nous n'en conservons qu'une{" "}
              <strong>empreinte irréversible</strong> — le lien lui-même n'est
              jamais stocké — ainsi que sa date d'expiration et, le cas échéant,
              sa date d'utilisation. Le lien <strong>expire au bout d'1 heure</strong>{" "}
              et <strong>ne fonctionne qu'une seule fois</strong>.
            </li>
            <li>
              <strong>Identifiant de session anonyme</strong> : identifiant
              technique aléatoire, stocké dans un cookie, sans création de
              compte.
            </li>
            <li>
              <strong>Localisation du site</strong> : adresse postale, latitude
              et longitude (saisie, avec suggestions d'adresse de la BAN).
            </li>
            <li>
              <strong>Paramètres d'installation</strong> : orientation,
              inclinaison, type et nombre de panneaux, configuration batterie et
              paramètres tarifaires.
            </li>
            <li>
              <strong>Résultats de simulation</strong> : production estimée,
              taux d'autoconsommation et d'autoproduction, indicateurs
              économiques.
            </li>
            <li>
              <strong>Courbe de consommation électrique</strong> : pas de 30
              minutes, jusqu'à 2 ans glissants, importée via fichier CSV{" "}
              <em>ou</em> récupérée via Enedis Data Connect avec votre
              consentement.
            </li>
            <li>
              <strong>Identifiant de point de livraison (PDL / PRM)</strong> — si
              vous utilisez Enedis Data Connect.
            </li>
            <li>
              <strong>Autorisation Enedis</strong> (si vous activez la
              récupération automatique) : une référence d'autorisation
              (identifiant opaque) mémorisant que vous avez autorisé l'accès et à
              quels compteurs. <strong>Ce n'est pas un identifiant de
              connexion</strong> : SunMatch s'authentifie auprès d'Enedis au
              niveau applicatif, <strong>aucun jeton de connexion personnel vous
              concernant n'est conservé</strong>.
            </li>
            <li>
              <strong>Suggestions / besoins</strong> (si vous utilisez le
              formulaire « Suggérer une idée / un besoin ») : le{" "}
              <strong>texte libre</strong> que vous saisissez, une{" "}
              <strong>catégorie</strong> optionnelle, et — seulement si vous le
              souhaitez — une <strong>adresse e-mail de recontact</strong>
              accompagnée de votre accord explicite pour être recontacté. L'e-mail
              n'est jamais obligatoire ; sans votre accord coché, il n'est pas
              conservé.
            </li>
          </ul>
          <p className="text-sm leading-relaxed text-slate-700">
            Votre courbe de consommation révèle des habitudes domestiques : elle
            est traitée comme une <strong>donnée personnelle comportementale</strong>{" "}
            et protégée comme telle.{" "}
            <strong>Aucune donnée sensible au sens de l'article 9 RGPD</strong>{" "}
            (santé, biométrie, opinions, etc.) n'est traitée. Aucune donnée
            bancaire, aucun cookie publicitaire ou de tracking.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            4. Finalités et base légale
          </h2>
          <p className="text-sm leading-relaxed text-slate-700">
            Vos données sont traitées <strong>exclusivement</strong> pour
            simuler le dimensionnement d'une installation photovoltaïque, en
            calculer les indicateurs (autoconsommation, autoproduction, gains
            économiques) et gérer votre compte le cas échéant. Aucune donnée
            n'est revendue, mutualisée, ni utilisée à des fins commerciales,
            publicitaires ou de profilage. Aucune décision automatisée
            produisant des effets juridiques n'est prise à votre égard.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            La base légale est votre <strong>consentement</strong> (article
            6.1.a du RGPD), recueilli avant tout traitement. La{" "}
            <strong>connexion via Google</strong> fait l'objet d'un consentement
            recueilli au <strong>premier login Google</strong> (case dédiée,
            préalable à la création du compte — identique à l'inscription par
            email et mot de passe) ; l'authentification Google elle-même relève
            de la relation directe entre vous et Google (voir § 5). La connexion
            à Enedis fait l'objet d'un <strong>consentement explicite et
            spécifique distinct</strong>, recueilli avant la redirection vers
            Enedis. Votre consentement est libre, éclairé et révocable à tout
            moment ; la version du texte accepté est horodatée comme preuve.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Réinitialisation du mot de passe.</strong> Elle{" "}
            <strong>ne repose sur aucune base légale nouvelle</strong> : c'est
            une opération de la <strong>gestion de votre compte</strong>, déjà
            couverte par le consentement recueilli à l'inscription. L'envoi de
            l'email est un <strong>moyen</strong>, pas une finalité nouvelle : le
            seul fait nouveau est l'intervention d'un{" "}
            <strong>prestataire</strong> (sous-traitant) pour l'acheminer (voir
            § 5).
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Suggestions et remontées de besoins.</strong> Lorsque vous
            envoyez une idée via le formulaire dédié, votre message est traité
            pour <strong>améliorer le produit et prioriser la feuille de
            route</strong>. La base légale est notre{" "}
            <strong>intérêt légitime</strong> (article 6.1.f) à recueillir vos
            retours ; vous pouvez vous y <strong>opposer</strong> à tout moment
            (article 21) et demander l'effacement de votre message. Si vous
            renseignez une adresse e-mail de recontact, celle-ci n'est traitée que
            sur la base de votre <strong>consentement explicite</strong> (article
            6.1.a), matérialisé par la case « J'accepte d'être recontacté » —
            sans cette case cochée, aucun e-mail n'est conservé.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            5. Destinataires et sous-traitants
          </h2>
          <p className="text-sm leading-relaxed text-slate-700">
            Aucun tiers commercial ne reçoit vos données. Les seuls intervenants
            sont :
          </p>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>
              <strong>Oracle Cloud Infrastructure</strong> (hébergeur) —
              hébergement de l'application et de la base de données en{" "}
              <strong>Union Européenne</strong> (région de Marseille, France).
            </li>
            <li>
              <strong>Scaleway SAS</strong> (France, région de Paris) —{" "}
              <strong>sous-traitant</strong> : achemine les emails de
              récupération d'accès à votre compte (lien de réinitialisation).
              Reçoit <strong>votre adresse email et le contenu du message</strong>,
              et rien d'autre — ni votre identifiant de compte, ni vos données de
              consommation.
            </li>
            <li>
              <strong>Enedis</strong> (Enedis Data Connect, France) — source de
              votre courbe de charge si vous activez la récupération
              automatique.
            </li>
            <li>
              <strong>BAN — Base Adresse Nationale</strong> (Etalab / DINUM,
              France) — suggestions et géocodage d'adresse, requêtes émises
              depuis votre navigateur.
            </li>
            <li>
              <strong>PVGIS / JRC</strong> (Commission européenne, Italie) —
              calcul de production solaire ; seules les coordonnées et
              l'orientation sont transmises, sans identifiant vous concernant.
            </li>
            <li>
              <strong>OpenStreetMap</strong> (fondation OSM, UE) — tuiles de la
              carte uniquement, pas de donnée personnelle transmise au-delà de
              la zone affichée.
            </li>
            <li>
              <strong>RTE</strong> (France) — calendrier Tempo public pour les
              calculs tarifaires ; aucune donnée vous concernant n'est
              transmise.
            </li>
            <li>
              <strong>Google</strong> (Google Ireland Ltd / Google LLC) —{" "}
              <strong>fournisseur d'identité</strong>, uniquement si vous utilisez
              « Continuer avec Google » : vérifie votre identité lors de la
              connexion et transmet à SunMatch votre email vérifié et un
              identifiant stable. Irlande (UE) / États-Unis.
            </li>
          </ul>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Envoi des emails de réinitialisation — Scaleway agit comme
            sous-traitant.</strong>{" "}
            Lorsque vous demandez la réinitialisation de votre mot de passe, nous
            confions l'<strong>acheminement</strong> de cet email à{" "}
            <strong>Scaleway SAS</strong> (société française, groupe Iliad), via
            son service d'envoi hébergé <strong>en France</strong>. Scaleway est
            un <strong>sous-traitant</strong> au sens de l'article 28 du RGPD :
            il traite ces données <strong>pour notre compte et sur nos
            instructions uniquement</strong>, sans finalité propre, et il est lié
            à nous par un <strong>contrat de sous-traitance (DPA)</strong>. Nous
            lui transmettons <strong>votre adresse email et le contenu du
            message</strong> — rien d'autre : ni votre identifiant de compte, ni
            vos données de consommation (nous ne détenons d'ailleurs ni votre
            nom, ni votre prénom). Le <strong>sujet</strong> de cet email est
            volontairement générique et ne contient aucune donnée vous
            concernant. Nous n'utilisons ce service <strong>que</strong> pour les
            emails liés à la <strong>gestion de l'accès à votre compte</strong> :
            jamais de newsletter, de relance, de notification produit ni de
            message publicitaire.
          </p>
          <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
            <strong>Google et Scaleway n'ont pas le même statut — et la
            différence est importante pour vous.</strong>{" "}
            <strong>Scaleway est un sous-traitant</strong> : il agit sur nos
            instructions et <strong>nous lui transmettons effectivement votre
            adresse email</strong>, encadré par un contrat (DPA) qui lui interdit
            tout autre usage. <strong>Google n'est PAS un sous-traitant</strong>{" "}
            (voir ci-dessous) : il agit pour son propre compte comme fournisseur
            d'identité, il n'y a <strong>pas de DPA</strong>, et{" "}
            <strong>nous ne lui transmettons rien</strong> — c'est vous qui vous
            authentifiez directement auprès de lui, et nous ne faisons que{" "}
            <strong>recevoir</strong> le résultat. Autrement dit : nous{" "}
            <em>confions</em> des données à Scaleway ; nous n'en confions{" "}
            <em>aucune</em> à Google.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Connexion via Google — statut particulier.</strong> Si, et
            seulement si, vous choisissez « Continuer avec Google », Google agit
            comme <strong>responsable de traitement indépendant</strong> de son
            propre service d'authentification (et <strong>non</strong> comme
            sous-traitant de SunMatch) : il n'y a <strong>pas de contrat de
            sous-traitance (DPA)</strong> entre SunMatch et Google pour cette
            fonctionnalité. C'est vous qui vous authentifiez directement auprès de
            Google ; SunMatch <strong>ne lui transmet aucune de vos données
            personnelles</strong> et se contente de recevoir, en retour, votre
            email vérifié et un identifiant technique stable. Votre interaction
            avec Google est régie par la{" "}
            <a
              href="https://policies.google.com/privacy"
              rel="noopener noreferrer"
              target="_blank"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              politique de confidentialité de Google
            </a>
            . Google étant susceptible de traiter certaines données aux
            États-Unis, ce traitement, propre à Google, s'appuie sur ses propres
            mécanismes de transfert (dont le cadre transatlantique de protection
            des données — <em>EU-US Data Privacy Framework</em>).
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Aucun transfert hors Union Européenne initié par
            SunMatch.</strong>{" "}
            SunMatch héberge et traite vos données exclusivement dans l'Union
            Européenne et n'exporte aucune de vos données hors UE. Cela vaut
            aussi pour l'<strong>envoi des emails</strong> de réinitialisation :
            le service est hébergé <strong>en France</strong> et nous avons
            vérifié la chaîne de sous-traitance de ce prestataire pour ce service
            précis — <strong>aucun intervenant hors UE</strong> n'y figure. Le
            seul cas où des données transitent par un service établi hors UE est
            la connexion Google <strong>que vous choisissez volontairement</strong>,
            décrite ci-dessus, et qui relève de la relation directe entre vous et
            Google.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            6. Durée de conservation
          </h2>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>
              Données de simulation, sites et consommation :{" "}
              <strong>12 mois</strong> après votre dernière activité, puis
              suppression.
            </li>
            <li>
              Adresse email et compte : tant que le compte existe ; suppression
              immédiate à la fermeture du compte.
            </li>
            <li>
              Lien de réinitialisation du mot de passe (empreinte irréversible et
              dates) : <strong>1 heure</strong>, puis suppression automatique. Le
              lien est en outre invalidé dès sa première utilisation, et supprimé
              avec votre compte.
            </li>
            <li>
              Trace de l'<strong>envoi</strong> de l'email chez notre prestataire
              d'envoi (sujet générique, date, adresse du destinataire) :{" "}
              <strong>durée non communiquée à ce jour</strong> par le prestataire
              — nous la lui avons demandée par écrit et publierons l'information
              dès que nous l'aurons. Ces traces ne contiennent ni le contenu du
              message, ni le lien.
            </li>
            <li>
              Autorisation Enedis (référence de consentement) : tant que votre
              consentement Enedis est actif. Suppression immédiate au retrait.
            </li>
            <li>
              Suggestions / remontées de besoins (message, catégorie, e-mail de
              recontact) : <strong>24 mois maximum</strong>, puis suppression
              automatique. Une suggestion rattachée à un compte ou à une session
              anonyme est aussi effacée lors de la suppression de ce compte ou de
              cette session.
            </li>
            <li>
              Preuve de consentement (version acceptée, horodatage) : conservée
              le temps de la relation puis jusqu'à 3 ans, à des fins de preuve.
            </li>
            <li>
              Journaux techniques (sans donnée personnelle) : 12 mois maximum.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            7. Vos droits
          </h2>
          <p className="text-sm leading-relaxed text-slate-700">
            Conformément aux articles 15 à 22 du RGPD, vous disposez des droits
            suivants : <strong>accès</strong>, <strong>rectification</strong>,{" "}
            <strong>effacement</strong> (art. 17), <strong>limitation</strong>,
            <strong> opposition</strong>, <strong>portabilité</strong> et{" "}
            <strong>retrait du consentement</strong> à tout moment (art. 7 § 3).
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Effacement en un clic</strong> depuis l'application :
          </p>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>
              <strong>Session anonyme</strong> : le bouton « Supprimer mes
              données » (écran Paramètres) efface en cascade tous vos sites,
              simulations, courbes de consommation et autorisations Enedis
              associées. Action irréversible.
            </li>
            <li>
              <strong>Compte</strong> : la suppression du compte efface, sans
              délai de grâce, votre compte et l'ensemble des données associées.
              Action irréversible.
            </li>
            <li>
              <strong>Enedis</strong> : vous pouvez déconnecter un site d'Enedis
              à tout moment ; l'autorisation Enedis correspondante est alors
              supprimée.
            </li>
          </ul>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>Une limite, que nous préférons vous dire.</strong> La
            suppression de votre compte efface vos données{" "}
            <strong>de nos systèmes</strong>, immédiatement et en cascade. En
            revanche, si vous avez demandé une réinitialisation de mot de passe,
            notre <strong>prestataire d'envoi d'emails</strong> conserve de son
            côté une <strong>trace technique de l'envoi</strong> (sujet
            générique, date, adresse du destinataire) que nous ne pouvons{" "}
            <strong>pas</strong> effacer à votre place : nous n'avons aucun moyen
            technique d'agir sur ses journaux. Cette trace{" "}
            <strong>ne contient ni le contenu du message, ni le lien de
            réinitialisation</strong>. Si vous souhaitez en demander
            l'effacement, écrivez-nous : nous relaierons votre demande auprès du
            prestataire.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            Pour les autres droits (accès, portabilité, limitation,
            opposition), écrivez à{" "}
            <a
              href="mailto:privacy@sunmatch.fr"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              privacy@sunmatch.fr
            </a>{" "}
            — réponse sous 30 jours maximum (délai légal RGPD).
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            Vous pouvez aussi déposer une réclamation auprès de la{" "}
            <a
              href="https://www.cnil.fr"
              rel="noopener noreferrer"
              target="_blank"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              CNIL
            </a>
            .
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            8. Sécurité
          </h2>
          <ul className="list-disc space-y-1 pl-6 text-sm leading-relaxed text-slate-700">
            <li>Mots de passe stockés de façon hachée, jamais en clair.</li>
            <li>
              <strong>Connexion Google</strong> : réalisée via le protocole
              standard <strong>OpenID Connect en redirection serveur</strong> —
              aucun script, mouchard ou SDK Google n'est chargé dans votre
              navigateur. Seuls votre email vérifié et un identifiant Google
              stable sont récupérés ; <strong>aucun jeton de connexion Google
              n'est conservé</strong>. Si l'email de votre connexion Google
              correspond à un compte existant protégé par mot de passe, la liaison{" "}
              <strong>exige la saisie de ce mot de passe</strong> : aucune
              association automatique n'est faite (protection contre l'usurpation
              de compte).
            </li>
            <li>
              <strong>Réinitialisation du mot de passe</strong> : le lien envoyé
              contient un code <strong>aléatoire, à usage unique</strong>, valable{" "}
              <strong>1 heure</strong>. Nous ne conservons de ce code qu'une{" "}
              <strong>empreinte irréversible</strong> — le code lui-même n'est
              stocké nulle part et n'apparaît dans <strong>aucun journal</strong>.
              Il est transporté dans une partie de l'adresse (le « fragment ») qui,
              par construction, <strong>n'est jamais transmise à nos serveurs</strong>{" "}
              ni à aucun intermédiaire technique. Réinitialiser votre mot de passe{" "}
              <strong>déconnecte toutes vos sessions</strong> ouvertes ailleurs.
              Enfin, la réponse affichée est <strong>rigoureusement identique</strong>{" "}
              que l'adresse saisie corresponde à un compte ou non : cette page ne
              permet pas de découvrir si quelqu'un est inscrit chez nous, et le
              nombre de demandes est limité (afin que personne ne puisse inonder
              d'emails la boîte d'un tiers).
            </li>
            <li>
              <strong>Accès Enedis</strong> : SunMatch s'authentifie auprès
              d'Enedis au niveau applicatif (OAuth2 Client Credentials) ;{" "}
              <strong>aucun jeton de connexion Enedis vous concernant n'est
              stocké</strong>. Le jeton technique applicatif est de courte durée
              et conservé uniquement en mémoire le temps des appels.
            </li>
            <li>
              HTTPS obligatoire sur tout le service et en-têtes de sécurité
              (CSP, HSTS, protection contre le détournement d'affichage).
            </li>
            <li>
              Cookies de session inaccessibles au JavaScript et transmis
              uniquement en HTTPS.
            </li>
            <li>
              Aucune donnée personnelle (consommation, coordonnées, jetons, mot
              de passe) ne figure dans les journaux techniques.
            </li>
            <li>
              Hébergement en Union Européenne uniquement, accès restreint au
              seul responsable de traitement.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">
            9. Modifications de cette politique
          </h2>
          <p className="text-sm leading-relaxed text-slate-700">
            Toute modification substantielle donne lieu à une{" "}
            <strong>nouvelle version</strong> de cette politique. Lorsque la
            modification porte sur une{" "}
            <strong>finalité soumise à votre consentement</strong>, elle vous est
            re-soumise via la fenêtre de consentement de l'application. Lorsqu'elle
            relève d'une <strong>information</strong> (ajout d'un traitement fondé
            sur l'intérêt légitime, d'un destinataire, ou précision de
            transparence), la nouvelle version est{" "}
            <strong>publiée ici sans re-solliciter votre consentement</strong> —
            l'intégrité de version (article 7 § 1) permet de savoir à quel texte
            chaque consentement se rapporte. Vous gardez à tout moment la
            possibilité de <strong>refuser</strong>, de vous{" "}
            <strong>opposer</strong> aux traitements fondés sur l'intérêt légitime
            et d'exercer votre <strong>droit à l'effacement</strong>.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>v2.4 (17 juillet 2026)</strong> — ajout de la{" "}
            <strong>réinitialisation du mot de passe par email</strong> (lien à
            usage unique, valable 1 heure). <strong>Aucune finalité ni base
            légale nouvelle</strong> : c'est une opération de la gestion de votre
            compte, déjà couverte par le consentement recueilli à l'inscription.
            Le fait nouveau est l'ajout d'un <strong>destinataire</strong> :{" "}
            <strong>Scaleway SAS</strong> (France) intervient comme{" "}
            <strong>sous-traitant</strong> pour acheminer cet email et reçoit
            votre adresse email — à ne pas confondre avec Google, qui n'est pas
            sous-traitant et à qui nous ne transmettons rien (§ 5). Mention
            également de la limite d'effacement relative aux traces d'envoi
            conservées par ce prestataire (§ 7), et de l'indétermination de leur
            durée de conservation, que nous lui avons demandée par écrit (§ 6).
            Mise à jour des sections 2, 3, 4, 5, 6, 7 et 8.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>v2.3 (14 juillet 2026)</strong> — ajout de la{" "}
            <strong>boîte à idées</strong> (formulaire « Suggérer une idée / un
            besoin ») : nouvelle finalité de gestion des suggestions, fondée sur
            l'intérêt légitime (avec droit d'opposition) pour votre message, et
            sur votre consentement explicite pour l'e-mail de recontact facultatif
            ; conservation 24 mois. Mise à jour des sections 3, 4 et 6.
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            <strong>v2.2 (14 juillet 2026)</strong> — ajout de la{" "}
            <strong>connexion via Google</strong> (OpenID Connect, facultative)
            comme méthode d'authentification : Google est mentionné comme
            fournisseur d'identité indépendant (pas un sous-traitant, pas de DPA)
            et seuls l'email vérifié et un identifiant stable sont récupérés.
            Mise à jour des sections 2, 3, 4, 5 et 8.
          </p>
        </section>

        <div className="pt-2">
          <Button asChild variant="outline">
            <Link to="/">Revenir à l'accueil</Link>
          </Button>
        </div>
      </Card>
    </article>
  );
}
